// Monokrome inline-SVG-ikoner, 20×20, arver `currentColor`.
//
// Reglene fra UX_DESIGNSYSTEM §6: ALDRI emoji, aldri Unicode-glyfer blandet med
// SVG i samme meny. Nytt verktøy uten ikon → legg det til her først.
//
// Bygget med `createElementNS` og ikke med en SVG-streng og `innerHTML`. Det er
// litt mer kode, men da gjelder «ingen innerHTML i src/» uten unntak — og et
// unntak er nettopp det som senere blir brukt på feltdata. (COP-ens
// `windows.ts` har et slikt unntak for pop-out-glyfen; her er den bygget som
// noder i stedet, se `popout`.)
//
// ══ HVA SOM ER MED HER, OG HVA SOM IKKE ER ═════════════════════════════════
// Her ligger BARE ikoner som ikke sier noe om domenet:
//   * flatens eget skall — `grep`, `dokk`, `popout`, `kryss` (panelmalen bruker
//     dem selv, så de MÅ bo sammen med den)
//   * de universelle tilstandene — `info`, `varsel`, `sjekk`, `last`, `nede`
//   * begreper enhver sensorflate har — `klokke` (alder/tidspunkt), `peiling`
//     (retning: en PIR, en akustisk peiler og en KrakenRF har alle en retning),
//     `maal` (målløsning), `kart`, `søk`, og `kamera` (både en kystpost og en
//     jegertropp har kamera)
//
// Artsikonene for ÉN sensorfamilie hører derimot i appen: `radar`, `rf`,
// `hydrofon` og fartøysymbolet `kontakt` er kystspesifikke og bor i
// `apps/njord/src/ui/ikoner.ts`. ULL får på samme vis sine egne (seismisk, pir,
// magnetisk, brytertråd). Grensa er «beskriver ikonet en sensorART eller et
// domeneobjekt?» — da er det appens, ellers er det flatens.
//
// Appene bygger sine egne ikoner med de SAMME byggeklossene (`ikonSvg`, `bane`,
// `rekt`, `sirkel`), slik at strekbredde, hjørner og viewBox er identiske på
// tvers av apper. Ett designsystem betyr også én strektykkelse.
const NS = 'http://www.w3.org/2000/svg';
/** Ikonrammen: 20×20, `currentColor`, dekorativ for skjermleser. */
export function ikonSvg(...baner) {
    const s = document.createElementNS(NS, 'svg');
    s.setAttribute('viewBox', '0 0 20 20');
    s.setAttribute('width', '20');
    s.setAttribute('height', '20');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '1.6');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    // Ikonet er dekorasjon ved siden av en tekstetikett; leses det opp i tillegg,
    // hører brukeren alt to ganger.
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('focusable', 'false');
    s.classList.add('ikon');
    for (const b of baner)
        s.appendChild(b);
    return s;
}
export function bane(d) {
    const e = document.createElementNS(NS, 'path');
    e.setAttribute('d', d);
    return e;
}
export function rekt(x, y, b, h, r = 1.5) {
    const e = document.createElementNS(NS, 'rect');
    e.setAttribute('x', String(x));
    e.setAttribute('y', String(y));
    e.setAttribute('width', String(b));
    e.setAttribute('height', String(h));
    e.setAttribute('rx', String(r));
    return e;
}
export function sirkel(cx, cy, r) {
    const e = document.createElementNS(NS, 'circle');
    e.setAttribute('cx', String(cx));
    e.setAttribute('cy', String(cy));
    e.setAttribute('r', String(r));
    return e;
}
export const IKONER = {
    /** Målløsning: kryss i sikte. */
    maal: () => ikonSvg(sirkel(10, 10, 6.5), bane('M10 1.5v3'), bane('M10 15.5v3'), bane('M1.5 10h3'), bane('M15.5 10h3'), sirkel(10, 10, 1.2)),
    /** Kamera: PTZ-hus på brakett. */
    kamera: () => ikonSvg(rekt(3, 6, 10, 7, 2), bane('M13 8.5l4-2v7l-4-2'), bane('M8 13v4'), bane('M5 17h6')),
    kart: () => ikonSvg(bane('M2.5 5.5L7 3.5l6 2 4.5-2v11l-4.5 2-6-2-4.5 2z'), bane('M7 3.5v11'), bane('M13 5.5v11')),
    peiling: () => ikonSvg(sirkel(10, 10, 7), bane('M10 10l4.5-4.5'), bane('M10 3v2')),
    klokke: () => ikonSvg(sirkel(10, 10, 7), bane('M10 6v4l2.5 2')),
    varsel: () => ikonSvg(bane('M10 3l7 13H3z'), bane('M10 8v3.5'), bane('M10 13.6v.2')),
    info: () => ikonSvg(sirkel(10, 10, 7), bane('M10 9v5'), bane('M10 6.3v.2')),
    sjekk: () => ikonSvg(bane('M4 10.5l4 4 8-9')),
    kryss: () => ikonSvg(bane('M5 5l10 10'), bane('M15 5L5 15')),
    last: () => ikonSvg(bane('M16 10a6 6 0 1 1-2-4.5'), bane('M16 3v3h-3')),
    nede: () => ikonSvg(sirkel(10, 10, 7), bane('M6.5 10h7')),
    søk: () => ikonSvg(sirkel(9, 9, 5.5), bane('M13 13l4 4')),
    /** Pop-out: ramme med pil ut. Samme betydning som COP-ens `⤢`. */
    popout: () => ikonSvg(bane('M11 3h6v6'), bane('M17 3l-7 7'), bane('M15 11.5V16a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 16V6.5A1.5 1.5 0 0 1 4.5 5H9')),
    /** Grep-hint i tittellinja: at panelet KAN dras skal være oppdagbart (§9). */
    grep: () => ikonSvg(sirkel(7.5, 5.5, 1), sirkel(12.5, 5.5, 1), sirkel(7.5, 10, 1), sirkel(12.5, 10, 1), sirkel(7.5, 14.5, 1), sirkel(12.5, 14.5, 1)),
    /** Sett panelet tilbake i rutenettet (motsatt av å løsne det). */
    dokk: () => ikonSvg(rekt(3, 4, 14, 12, 2), bane('M3 8h14'), bane('M10 8v8')),
};
/** Hent et ikon. Alltid en ny node — SVG-er kan ikke deles mellom to steder. */
export function ikon(navn) {
    return IKONER[navn]();
}
export function somIkon(kilde) {
    return typeof kilde === 'string' ? ikon(kilde) : kilde;
}
//# sourceMappingURL=ikoner.js.map